import { Temporal } from 'temporal-polyfill'
import { format as dateFnsFormatDate } from 'date-fns/format'
import { frCA as dateFnsFrCa } from 'date-fns/locale/fr-CA'
import { enCA as dateFnsEnCa } from 'date-fns/locale/en-CA'
import { toZonedTime } from 'date-fns-tz'

import Language from '../../app/Language.enum'
import { useLanguage } from '../../modules/i18n'
import { useTranslation } from 'react-i18next'

export const formatDate = (date?: Date | string | null, format = 'y-MM-dd') => {
  if (!date) {
    return
  }
  const dateObject = typeof date === 'string' ? new Date(date) : date
  return dateFnsFormatDate(dateObject, format)
}

export const formatDateTime = (date?: Date | string | null) => {
  return formatDate(date, 'y-MM-dd H:mm:ss')
}

export const useDateFnsLocale = (language: string) => {
  switch (language) {
    case Language.Fr:
      return dateFnsFrCa
    case Language.En:
    default:
      return dateFnsEnCa
  }
}

type FormatDateOptions = {
  showYear?: boolean
  shortMonth?: boolean
  shortDay?: boolean
  showTime?: boolean
}

type FormatDateOptionsWithInterval = FormatDateOptions & {
  className?: string
  dateClassName?: string
  fromToClassName?: string
}

/**
 * date formatter that returns a string
 */
export const useDateStringFormatter = (language?: string) => {
  const currentLanguage = useLanguage()
  language ??= currentLanguage ?? Language.En
  const locale = useDateFnsLocale(language)

  // eslint-disable-next-line react/display-name
  return (
    date?: any,
    {
      showYear = true,
      shortMonth = false,
      shortDay = false,
    }: FormatDateOptions = {},
  ): string | undefined => {
    if (!date) {
      return undefined
    }
    if (typeof date === 'string') {
      date = new Date(date)
    }

    let pattern: string
    switch (language) {
      case Language.Fr: {
        const month = shortMonth ? 'MMM' : 'MMMM'
        pattern = showYear ? `d ${month} y` : `d ${month}`
        break
      }
      case Language.En:
      default: {
        const month = shortMonth ? 'MMM.' : 'MMMM'
        const day = shortDay ? 'd' : 'do'
        pattern = showYear ? `${month} ${day}, y` : `${month} ${day}`
      }
    }

    if (date instanceof Date && !isNaN(date.getTime())) {
      return dateFnsFormatDate(date, pattern, { locale })
    }
  }
}

/**
 * date formatter that returns a string or React node
 */
export const useDateFormatter = (language?: string) => {
  const { t } = useTranslation()
  const currentLanguage = useLanguage()
  language ??= currentLanguage ?? Language.En
  const formatDateAsString = useDateStringFormatter()

  const format = (date?: any, {
    showTime,
    ...options
  }: FormatDateOptions = {}) => {
    const formatted = formatDateAsString(date, options)
    if (!formatted) {
      return undefined
    }
    const time = showTime ? ` ${formatDate(date, 'H:mm:ss') ?? ''}` : ''
    if (language === Language.Fr && formatted.substring(0, 2) === '1 ') {
      return <>1<sup>er</sup>{ formatted.substring(1) }{ time }</>
    }
    return `${formatted}${time}`
  }

  // eslint-disable-next-line react/display-name
  return (
    date?: any,
    {
      className,
      dateClassName,
      fromToClassName = 'text-sm text-neutral-400 lowercase',
      ...options
    }: FormatDateOptionsWithInterval = {},
  ): string | React.ReactNode => {
    const forceComponent = !!className || !!dateClassName || !!fromToClassName

    if (typeof date === 'object' && 'from' in date) {
      if ('to' in date && !!date.to) {
        const from = format(date.from, { showYear: false, shortMonth: true, ...options })
        const to = format(date.to, { shortMonth: true, ...options })
        if (!forceComponent && typeof from === 'string' && typeof to === 'string') {
          return `${t('date.from')} ${from} ${t('date.to')} ${to}`
        }
        return (
          <span className={className}>
            <span className={fromToClassName}>
              { t('date.from') }
              { ' ' }
            </span>
            <span className={dateClassName}>
              { from }
            </span>
            <span className={fromToClassName}>
              { ' ' }
              { t('date.to') }
              { ' ' }
            </span>
            <span className={dateClassName}>
              { to }
            </span>
          </span>
        )
      }
      return format(date.from, options)
    }
    return format(date, options)
  }
}

/**
 * ignore time zone of a utc date
 */
export const ignoreTimezone = (date?: string | Date) => {
  if (!date) {
    return undefined
  }
  const dateString = typeof date === 'string' ? date : date.toISOString()
  return new Date(dateString.substring(0, dateString.length - 1))
}

/**
 * return utc date in given timezone
 */
export const dateInTimezone = (date: string | Date, timezone?: string) => {
  if (!timezone) {
    return new Date(date)
  }
  return toZonedTime(date, timezone)
}

export const convertTemporalPlainDateToJsDate = (date: string, plainTime?: Temporal.PlainTime) => {
  const plainDateTime = Temporal.PlainDate.from(date).toZonedDateTime({
    timeZone: Temporal.Now.timeZoneId(),
    plainTime,
  })
  return new Date(plainDateTime.epochMilliseconds)
}
